import React, { useEffect, useMemo, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { Link } from "react-router-dom";
import { apiRequest } from "../helpers/api";
import { SkeletonCalendar, SkeletonList, SkeletonRegion } from "../components/skeleton/Skeleton";
import AuthenticatedMedia from "../components/AuthenticatedMedia";
import { ToastFeedback } from "../components/ToastNotifications";
import "./ContentCalendar.css";
import { Can } from "../components/AccessContext";
import { studioForPost } from "../components/postStudio/postStudioUtils";

const toDateTimeInput = (date) => {
  const value = new Date(date);
  if (Number.isNaN(value.getTime())) return "";
  const local = new Date(value.getTime() - value.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const getMonthRange = (date) => {
  const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
  const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  const start = new Date(monthStart);
  const end = new Date(monthEnd);
  start.setDate(start.getDate() - start.getDay());
  end.setDate(end.getDate() + (6 - end.getDay()) + 1);
  return { start: start.toISOString(), end: end.toISOString() };
};

const platformLabel = (platform) => platform ? platform.replaceAll("_", " ") : "Social post";

const getPostTitle = (post) => post.title?.trim() || post.topic?.trim() || post.caption?.trim()?.split(/\s+/).slice(0, 9).join(" ") || "Untitled post";

const getStatusClass = (status) => String(status || "DRAFT").toLowerCase().replaceAll("_", "-");

const ContentCalendar = () => {
  const calendarRef = useRef(null);
  const requestSequence = useRef(0);
  const [posts, setPosts] = useState([]);
  const [connectedPlatforms, setConnectedPlatforms] = useState([]);
  const [visibleRange, setVisibleRange] = useState(() => getMonthRange(new Date()));
  const [refreshKey, setRefreshKey] = useState(0);
  const [visibleDate, setVisibleDate] = useState(new Date());
  const [platformFilter, setPlatformFilter] = useState("all");
  const [schedulablePosts, setSchedulablePosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [scheduleTarget, setScheduleTarget] = useState(null);
  const [selectedExistingPost, setSelectedExistingPost] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [loading, setLoading] = useState(true);
  const [initialised, setInitialised] = useState(false);
  const [loadingAvailable, setLoadingAvailable] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let active = true;
    apiRequest("/api/social/accounts")
      .then(({ accounts = [] }) => {
        if (!active) return;
        setConnectedPlatforms([...new Set(accounts.filter((account) => account.status === "CONNECTED").map((account) => account.platform).filter(Boolean))]);
      })
      .catch(() => {
        if (active) setConnectedPlatforms([]);
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!visibleRange) return undefined;
    const requestId = ++requestSequence.current;
    let active = true;
    setLoading(true);
    setError("");
    const query = new URLSearchParams({
      start: visibleRange.start,
      end: visibleRange.end,
      status: "SCHEDULED"
    });
    apiRequest(`/api/posts?${query.toString()}`)
      .then(({ posts: scheduledPosts = [] }) => {
        if (active && requestId === requestSequence.current) setPosts(scheduledPosts);
      })
      .catch((requestError) => {
        if (active && requestId === requestSequence.current) setError(requestError.message);
      })
      .finally(() => {
        if (active && requestId === requestSequence.current) { setLoading(false); setInitialised(true); }
      });
    return () => { active = false; };
  }, [visibleRange, refreshKey]);

  useEffect(() => {
    if (!selectedPost && !scheduleTarget) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape" && !saving) {
        setSelectedPost(null);
        setScheduleTarget(null);
      }
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [selectedPost, scheduleTarget, saving]);

  const platforms = useMemo(() => [...new Set([
    ...connectedPlatforms,
    ...posts.map((post) => post.platform).filter(Boolean)
  ])].sort(), [connectedPlatforms, posts]);

  const filteredPosts = useMemo(() => platformFilter === "all"
    ? posts
    : posts.filter((post) => post.platform === platformFilter), [platformFilter, posts]);

  const events = useMemo(() => filteredPosts.map((post) => ({
    id: String(post.id),
    title: getPostTitle(post),
    start: post.scheduledAt,
    allDay: false,
    extendedProps: { post },
    classNames: [`calendar-event-${getStatusClass(post.status)}`]
  })), [filteredPosts]);

  const setCalendarDate = (date) => calendarRef.current?.getApi().gotoDate(date);

  const navigateCalendar = (direction) => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) return;
    if (direction === "today") calendarApi.today();
    else if (direction === "next") calendarApi.next();
    else calendarApi.prev();
    const nextDate = calendarApi.getDate();
    setVisibleDate(nextDate);
    setVisibleRange(getMonthRange(nextDate));
  };

  const selectCalendarMonth = (value) => {
    if (!value) return;
    const [year, month] = value.split("-").map(Number);
    const nextDate = new Date(year, month - 1, 1);
    setCalendarDate(nextDate);
    setVisibleDate(nextDate);
    setVisibleRange(getMonthRange(nextDate));
  };

  const handleDatesSet = ({ start, end, view }) => {
    setVisibleDate(view.currentStart || start);
    setVisibleRange((current) => {
      const nextStart = start.toISOString();
      const nextEnd = end.toISOString();
      return current?.start === nextStart && current?.end === nextEnd
        ? current
        : { start: nextStart, end: nextEnd };
    });
  };

  const openAddExistingDialog = async (date = new Date()) => {
    setScheduleTarget(date);
    setSelectedPost(null);
    setSelectedExistingPost("");
    setScheduledAt(toDateTimeInput(date));
    setLoadingAvailable(true);
    setError("");
    try {
      const { posts: available = [] } = await apiRequest("/api/posts/schedulable");
      setSchedulablePosts(available);
      if (available.length) setSelectedExistingPost(String(available[0].id));
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoadingAvailable(false);
    }
  };

  const scheduleExistingPost = async (event) => {
    event.preventDefault();
    if (!selectedExistingPost || !scheduledAt) {
      setError("Select an existing post and a date and time.");
      return;
    }
    const selectedDate = new Date(scheduledAt);
    if (Number.isNaN(selectedDate.getTime()) || selectedDate <= new Date()) {
      setError("Choose a future date and time.");
      return;
    }
    setSaving(true);
    setError("");
    setNotice("");
    try {
      await apiRequest(`/api/posts/${selectedExistingPost}/schedule`, {
        method: "POST",
        body: JSON.stringify({ scheduledAt: selectedDate.toISOString() })
      });
      setScheduleTarget(null);
      setNotice("Existing post added to the calendar.");
      setCalendarDate(selectedDate);
      setRefreshKey((value) => value + 1);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const saveReschedule = async (event) => {
    event.preventDefault();
    if (!selectedPost || !scheduledAt) return;
    const nextDate = new Date(scheduledAt);
    if (Number.isNaN(nextDate.getTime()) || nextDate <= new Date()) {
      setError("Choose a future date and time.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await apiRequest(`/api/posts/${selectedPost.id}/schedule`, {
        method: "POST",
        body: JSON.stringify({ scheduledAt: nextDate.toISOString() })
      });
      setSelectedPost(null);
      setNotice("Post rescheduled.");
      setCalendarDate(nextDate);
      setRefreshKey((value) => value + 1);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEventDrop = async ({ event, revert }) => {
    try {
      await apiRequest(`/api/posts/${event.id}/schedule`, {
        method: "POST",
        body: JSON.stringify({ scheduledAt: event.start.toISOString() })
      });
      setNotice("Post rescheduled.");
      setRefreshKey((value) => value + 1);
    } catch (requestError) {
      revert();
      setError(requestError.message);
    }
  };

  const openPostDetails = ({ event }) => {
    const post = event.extendedProps.post;
    setSelectedPost(post);
    setScheduleTarget(null);
    setScheduledAt(toDateTimeInput(post.scheduledAt));
    setError("");
  };

  const goToToday = () => navigateCalendar("today");
  const goToPrevious = () => navigateCalendar("previous");
  const goToNext = () => navigateCalendar("next");
  const monthValue = `${visibleDate.getFullYear()}-${String(visibleDate.getMonth() + 1).padStart(2, "0")}`;
  const monthTitle = visibleDate.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const closeDialogs = () => {
    if (saving) return;
    setSelectedPost(null);
    setScheduleTarget(null);
    setError("");
  };

  const eventContent = ({ event, timeText }) => {
    const post = event.extendedProps.post;
    const thumbnail = post.imageUrl || post.videoThumbnailUrl;
    return (
      <div className="calendar-event-content" title={`${platformLabel(post.platform)} · ${post.caption || ""}`}>
        <span className="calendar-event-thumb">
          {thumbnail
            ? <AuthenticatedMedia src={thumbnail} alt="" />
            : <i className={`ti ${post.videoUrl ? "ti-video" : "ti-photo"}`} aria-hidden="true" />}
        </span>
        <span className="calendar-event-copy">
          <span className="calendar-event-topline"><span className="calendar-event-platform">{platformLabel(post.platform)}</span><span className="calendar-event-time">{timeText || ""}</span></span>
          <strong>{getPostTitle(post)}</strong>
          <span className={`calendar-event-status status-${getStatusClass(post.status)}`}>{String(post.status || "SCHEDULED").replaceAll("_", " ")}</span>
        </span>
      </div>
    );
  };

  return (
    <section className="content-calendar-page">
      <header className="calendar-page-header">
        <div><p className="calendar-eyebrow">PUBLISHING</p><h1>Content Calendar</h1><p>Review your scheduled posts and keep publishing plans on track.</p></div>
        <Can permission={["user.posts.schedule", "user.content_calendar.schedule"]}><button type="button" className="btn btn-primary calendar-add-button" onClick={() => openAddExistingDialog(new Date())}>
          <i className="ti ti-calendar-plus" aria-hidden="true" /> Add Existing Post
        </button></Can>
      </header>

      {error && <ToastFeedback type="error" message={error} />}
      {notice && <ToastFeedback type="success" message={notice} />}

      <div className="calendar-controls">
        <div className="calendar-navigation" aria-label="Calendar navigation">
          <button type="button" className="btn btn-light calendar-icon-button" onClick={goToPrevious} aria-label="Previous month" title="Previous month"><i className="ti ti-chevron-left" /></button>
          <button type="button" className="btn btn-light calendar-today-button" onClick={goToToday}>Today</button>
          <button type="button" className="btn btn-light calendar-icon-button" onClick={goToNext} aria-label="Next month" title="Next month"><i className="ti ti-chevron-right" /></button>
        </div>
        <h2 className="calendar-current-month">{monthTitle}</h2>
        <div className="calendar-filters">
          <div className="calendar-filter-field">
            <label htmlFor="calendar-month-filter">Month</label>
            <input id="calendar-month-filter" className="form-control" type="month" value={monthValue} onChange={(event) => selectCalendarMonth(event.target.value)} />
          </div>
          <div className="calendar-filter-field">
            <label htmlFor="calendar-platform-filter">Social platform</label>
            <select id="calendar-platform-filter" className="form-select" value={platformFilter} onChange={(event) => setPlatformFilter(event.target.value)}>
              <option value="all">All platforms</option>
              {platforms.map((platform) => <option value={platform} key={platform}>{platformLabel(platform)}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="calendar-legend" aria-label="Post status legend">
        <span><i className="legend-dot status-scheduled" /> Scheduled</span>
        <span><i className="legend-dot status-publishing" /> Publishing</span>
        <span><i className="legend-dot status-published" /> Published</span>
        <span><i className="legend-dot status-failed" /> Failed</span>
        {loading && initialised && <span className="calendar-loading-note" aria-live="polite">Updating calendar...</span>}
      </div>

      <section className="calendar-board" aria-label="Scheduled posts calendar">
        {!initialised && <div className="sk-overlay"><SkeletonRegion label="Loading calendar"><SkeletonCalendar /></SkeletonRegion></div>}
        <div className="calendar-scroll-area">
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            initialDate={new Date()}
            headerToolbar={false}
            events={events}
            datesSet={handleDatesSet}
            dateClick={({ date }) => openAddExistingDialog(date)}
            eventClick={openPostDetails}
            eventDrop={handleEventDrop}
            editable
            eventDurationEditable={false}
            dayMaxEvents={3}
            fixedWeekCount={false}
            showNonCurrentDates
            height="auto"
            eventContent={eventContent}
            eventTimeFormat={{ hour: "numeric", minute: "2-digit", meridiem: "short" }}
            dayCellClassNames={({ date }) => {
              const today = new Date();
              return date.getFullYear() === today.getFullYear() && date.getMonth() === today.getMonth() && date.getDate() === today.getDate()
                ? ["calendar-current-day"]
                : [];
            }}
            moreLinkClick="popover"
          />
        </div>
      </section>
      <p className="calendar-helper-text">Select a date to schedule an existing post, or drag a scheduled post to move it.</p>

      {(selectedPost || scheduleTarget) && <div className="calendar-dialog-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) closeDialogs(); }}>
        {selectedPost ? <section className="calendar-dialog" role="dialog" aria-modal="true" aria-labelledby="post-detail-title">
          <header className="calendar-dialog-header">
            <div><p className="calendar-eyebrow">{platformLabel(selectedPost.platform)}</p><h2 id="post-detail-title">Post details</h2></div>
            <button type="button" className="calendar-dialog-close" aria-label="Close post details" onClick={closeDialogs}>×</button>
          </header>
          <div className="calendar-post-detail">
            {(selectedPost.imageUrl || selectedPost.videoUrl) && <div className="calendar-detail-media">
              {selectedPost.imageUrl
                ? <AuthenticatedMedia src={selectedPost.imageUrl} alt="Post media" />
                : <AuthenticatedMedia src={selectedPost.videoUrl} video controls muted={false} />}
            </div>}
            <div className="calendar-detail-meta">
              <span className={`calendar-detail-status status-${getStatusClass(selectedPost.status)}`}>{String(selectedPost.status).replaceAll("_", " ")}</span>
              <span>{selectedPost.generationSource === "AI" ? "AI Post Studio" : "Manual Post Studio"}</span>
              {selectedPost.contentType && <span>{selectedPost.contentType}</span>}
              <span>{selectedPost.scheduledAt ? new Date(selectedPost.scheduledAt).toLocaleString() : "Not scheduled"}</span>
            </div>
            <h3>{getPostTitle(selectedPost)}</h3>
            <p className="calendar-detail-caption">{selectedPost.caption}</p>
            {selectedPost.hashtags?.length > 0 && <p className="calendar-detail-hashtags">{Array.isArray(selectedPost.hashtags) ? selectedPost.hashtags.join(" ") : selectedPost.hashtags}</p>}
            <Can permission="user.posts.edit"><Link className="calendar-studio-link" to={studioForPost(selectedPost).edit(selectedPost.id)}><i className="ti ti-pencil" aria-hidden="true" /> Edit in {selectedPost.generationSource === "AI" ? "AI Post Studio" : "Manual Post Studio"}</Link></Can>
          </div>
          <form className="calendar-dialog-form" onSubmit={saveReschedule}>
            <label htmlFor="reschedule-date">Change date and time</label>
            <div className="calendar-dialog-schedule-row">
              <input id="reschedule-date" className="form-control" type="datetime-local" value={scheduledAt} onChange={(event) => setScheduledAt(event.target.value)} required />
              <Can permission={["user.posts.schedule", "user.content_calendar.schedule"]}><button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Saving..." : "Save time"}</button></Can>
            </div>
            <button type="button" className="btn btn-link calendar-modal-secondary" onClick={closeDialogs}>Close</button>
          </form>
        </section> : <section className="calendar-dialog" role="dialog" aria-modal="true" aria-labelledby="add-existing-title">
          <header className="calendar-dialog-header">
            <div><p className="calendar-eyebrow">SCHEDULE AN EXISTING POST</p><h2 id="add-existing-title">Add Existing Post</h2></div>
            <button type="button" className="calendar-dialog-close" aria-label="Close schedule dialog" onClick={closeDialogs}>×</button>
          </header>
          {loadingAvailable ? <SkeletonRegion label="Loading saved posts"><SkeletonList rows={3} meta={false} /></SkeletonRegion> : schedulablePosts.length ? <form className="calendar-dialog-form" onSubmit={scheduleExistingPost}>
            <label htmlFor="existing-post-select">Saved post</label>
            <select id="existing-post-select" className="form-select calendar-existing-post-select" value={selectedExistingPost} onChange={(event) => setSelectedExistingPost(event.target.value)} required>
              {schedulablePosts.map((post) => <option value={post.id} key={post.id}>{platformLabel(post.platform)} · {getPostTitle(post)}</option>)}
            </select>
            {selectedExistingPost && (() => {
              const post = schedulablePosts.find((item) => String(item.id) === selectedExistingPost);
              return post ? <div className="calendar-existing-preview">
                {post.imageUrl ? <AuthenticatedMedia src={post.imageUrl} alt="" /> : <span className="calendar-existing-placeholder"><i className="ti ti-photo" /></span>}
                <div><strong>{getPostTitle(post)}</strong><span>{post.caption}</span></div>
              </div> : null;
            })()}
            <label htmlFor="existing-post-date">Schedule date and time</label>
            <input id="existing-post-date" className="form-control" type="datetime-local" value={scheduledAt} onChange={(event) => setScheduledAt(event.target.value)} required />
            <div className="calendar-dialog-actions">
              <button type="button" className="btn btn-light" onClick={closeDialogs} disabled={saving}>Cancel</button>
              <Can permission={["user.posts.schedule", "user.content_calendar.schedule"]}><button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Scheduling..." : "Add to calendar"}</button></Can>
            </div>
          </form> : <div className="calendar-no-existing-posts">
            <i className="ti ti-calendar-plus" aria-hidden="true" />
            <h3>No existing posts available</h3>
            <p>Saved draft or approved posts will appear here for scheduling.</p>
            <button type="button" className="btn btn-light" onClick={closeDialogs}>Close</button>
          </div>}
        </section>}
      </div>}
    </section>
  );
};

export default ContentCalendar;
